import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-final-form';
import React, { useCallback, useMemo, useRef } from 'react';
import { NewRadioButton } from '../../../NewRadioButton';
import { Select } from '../../../ui/Select';
import {
  capitalizeFirstLetter,
  getBasedDataStatValueInFilters
} from '../../../../utils/helpers/common';
import { TypeFilterContainer, popoverStyles } from './styled';
import { BUILDING_TYPE_ID } from '../../../../utils/helpers/constants';
import {
  getDefaultTypeFilterOptions,
  getTypeFilterOptions,
  hasOneFlatCategory,
  normalizeCategoryId
} from '../../../../utils/helpers/getTypeFilterOptions';
import { FilterFieldProps, SelectRefType } from '../types';
import useScreenSize from '../../../../hooks/useScreenSize';
import { NewSearchCategories } from '../../NewSearchCategories';
import { useClient } from '../../../../hooks/useClient';

export const BuildingTypeFilter: React.FC<FilterFieldProps> = ({
  submitOnChange = false,
  isPashaLanding,
  categories
}) => {
  const { t } = useTranslation();
  const { values } = useFormState({
    subscription: { values: true }
  });
  const { change, submit } = useForm();
  const { isSmall, isLarge } = useScreenSize();
  const isClient = useClient();
  const selectRef = useRef<SelectRefType>(null);
  const basedDataStat = getBasedDataStatValueInFilters(isPashaLanding);

  const selectedCategoryId = useMemo(() => {
    const hasPashaOneFlatCategory = hasOneFlatCategory(
      categories,
      isPashaLanding
    );
    return normalizeCategoryId(values.category_id, hasPashaOneFlatCategory);
  }, [categories, isPashaLanding, values.category_id]);

  const selectedValue = useMemo(
    () =>
      capitalizeFirstLetter(
        t(`pages.current_item.categories.id_${selectedCategoryId}`)
      ),
    [t, values.category_id]
  );

  const typePlaceholder = useMemo(
    () => t('pages.current_item.building_type'),
    [t]
  );

  const handleChangeOption = useCallback(
    (itemId: string) => {
      change('category_id', itemId);
      selectRef.current?.closePopover();
      if (submitOnChange) submit();
    },
    [change, submit, submitOnChange]
  );

  const options = categories
    ? getTypeFilterOptions(categories, basedDataStat, isPashaLanding)
    : getDefaultTypeFilterOptions(BUILDING_TYPE_ID) || [];

  const fullWidth = isPashaLanding && isClient && isLarge;
  const childrenTypes = [
    BUILDING_TYPE_ID.FLAT_NEW,
    BUILDING_TYPE_ID.FLAT_SECONDARY
  ];

  return (
    <Select
      ref={selectRef}
      placeholder={typePlaceholder}
      value={selectedValue}
      cypress={`${basedDataStat}-type-filter`}
      width={fullWidth ? '100%' : 195}
      modalTitle={t('new_search.type_estate')}
      modalCypress="filter-modal"
      showModalHeaderBorder={!isPashaLanding}
      showIcon={options.length > 1}
      popoverStyles={popoverStyles}
      valueTag="body_1"
    >
      <TypeFilterContainer>
        {(isClient && !isSmall) || isPashaLanding ? (
          options.map(type => (
            <NewRadioButton
              key={type.data.id}
              data={{
                ...type.data,
                name: capitalizeFirstLetter(t(type.data.name))
              }}
              currentValue={values.category_id}
              onChangeHandler={() => handleChangeOption(type.data.id)}
              name={type.name}
              cypress={type.name}
              stat={type.name}
              containerClassNames={
                !isPashaLanding && childrenTypes.includes(type.data.id)
                  ? 'children-type'
                  : ''
              }
            />
          ))
        ) : (
          <NewSearchCategories
            categories={categories}
            onClick={handleChangeOption}
            selectedValue={values.category_id}
          />
        )}
      </TypeFilterContainer>
    </Select>
  );
};
